import * as THREE from 'three';
import { VEHICLES, stockColorOf } from '@/entities/vehicles';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';
import { applyCarMaterials } from '@/entities/CarView';
import { wheelCorner } from '@/entities/livery/wheels';
import { attachModRig, type ModRig } from '@/entities/mods/ModRig';
import { surfaceFromObject, type SurfaceField } from '@/entities/mods/modSurface';
import type { CarLook } from '@/entities/carLook';
import { EnvironmentBuilder } from '@/render/Environment';
import { ACCENTS } from '@/ui/vehicleCatalog';
import { yieldToMain } from '@/core/scheduler';
import { span, recordSpan } from '@/dev/perf';

/**
 * GarageStage.ts — the GARAGE showroom (M68), the offline sibling of the
 * online LobbyStage. A transparent full-screen canvas over the F1-pit
 * backdrop (public/garage/bg.webp): the ACTIVE ride rotates on the hero
 * pedestal front-centre while every other ride waits in a lineup behind it —
 * unlocked rides lit in their own livery, locked rides as dark GHOSTS
 * (see-through, dim ring) with a DOM padlock chip anchored by the screen.
 *
 * Placement is the M64 screen-driven trick: the screen hands each bay a
 * viewport-fraction target and the stage unprojects it onto the ground plane
 * (lower on screen = nearer = bigger). Bays GLIDE between spots (damped), so
 * picking a new ride visibly drives it forward while the old hero backs into
 * the vacated slot.
 */

const ROT_SPEED = 0.55; // rad/s of hero pedestal spin
/** M114 — the hero lift's target on-screen span in world units (scale×length
 *  caps here; 5.9 = the 3.8u coupe at the tuned 1.55 lift). */
const HERO_SPAN = 5.9;
const LINEUP_YAW = -0.42; // parked 3/4 angle for the background lineup
const GLIDE = 5.5; // 1/s — exponential damp rate for bay position/yaw
/** Locked rides render as dark translucent ghosts. */
const GHOST_PAINT = 0x4a5468;
const GHOST_OPACITY = 0.45;

/** M115 — the WHEEL CLOSE-UP (the shop's TIRES & RIMS tab on phones): the hero
 *  parks dead SIDE-ON so a wheel faces the camera and STOPS turning on its
 *  pedestal (a rotating car swings the wheel straight out of an ultra-tight
 *  frame — the whole reason the owner asked for the spin to stop). The car
 *  model faces local +Z, so a -90° yaw swings local +X (the near flank)
 *  toward the camera at world +Z. */
const WHEEL_YAW = -Math.PI / 2;
/** Slow "on the rollers" wheel roll, rad/s — the only motion in the close-up. */
const WHEEL_SPIN = 0.6;

export interface GarageBayDef {
  id: string;
  unlocked: boolean;
  /** M79 — the equipped cosmetic look this bay's car wears (shop + garage). */
  look?: CarLook;
}

export interface BayTarget {
  /** Viewport fractions (0..1) where this bay's ground point should project. */
  xFrac: number;
  yFrac: number;
}

/** M115 — where a single wheel should land, and how big it should read. */
export interface WheelCloseUp {
  /** Viewport fractions (0..1) the chosen wheel's HUB lands on, exactly. */
  xFrac: number;
  yFrac: number;
  /** Fraction of the viewport HEIGHT the wheel's diameter should fill. */
  fill: number;
}

/** One rollable wheel of a loaded GLB (the M79c naming contract). */
interface BayWheel {
  pivot: THREE.Group;
  axis: THREE.Vector3;
  /** Hub centre in carHolder-local space (so it survives the pedestal spin). */
  hub: THREE.Vector3;
  /** Tyre radius in carHolder-local units. */
  radius: number;
}

interface Bay {
  id: string;
  unlocked: boolean;
  look?: CarLook;
  /** M128: the equipped mods hanging off the car holder (rebuilt per look). */
  rig: ModRig | null;
  /** M128c: the measured top surface of the bay's model (null until it lands). */
  surface?: SurfaceField | null;
  group: THREE.Group;
  carHolder: THREE.Group;
  /** The fitted GLB root once loaded — kept so a look swap can re-skin it
   *  in place (M79 shop preview) instead of reloading the model. */
  modelRoot: THREE.Object3D | null;
  paintColor: number;
  ringMat: THREE.MeshBasicMaterial;
  shadowMat: THREE.MeshBasicMaterial;
  loadToken: number;
  target: THREE.Vector3;
  yawTarget: number;
  spinPhase: number;
  isHero: boolean;
  placed: boolean;
  /** M115 — rollable wheels of the loaded GLB (empty for the procedural
   *  fallback body, which is what makes the close-up degrade gracefully). */
  wheels: BayWheel[];
  wheelPhase: number;
}

/**
 * M115 — group each corner's meshes (Tyre_/Rim_/Trim_barrel_ + the shared id
 * suffix, the M79c contract the vehicle GLBs ship) under a pivot at its hub so
 * the real model wheels can roll, and record the hub + tyre radius in
 * carHolder-local space so the close-up can frame one exactly. Mirrors
 * CarView.spinifyWheels (same contract, different scene graph: this stage owns
 * no CarView, and its cars are per-bay clones, so wrapping here is safe).
 */
function collectWheels(fitted: THREE.Object3D, holder: THREE.Group): BayWheel[] {
  holder.updateWorldMatrix(true, true);
  // Every wheel rolls about the car's left-right axis (holder-local X).
  const axleWorld = new THREE.Vector3(1, 0, 0).transformDirection(holder.matrixWorld).normalize();
  const invHolder = holder.matrixWorld.clone().invert();

  const groups = new Map<string, { meshes: THREE.Mesh[]; tyre: THREE.Mesh | null }>();
  fitted.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const match = /^(Tyre|Rim|Trim_barrel)_(.+)$/.exec(m.name);
    if (!match) return;
    const key = wheelCorner(match[2]);
    let e = groups.get(key);
    if (!e) groups.set(key, (e = { meshes: [], tyre: null }));
    e.meshes.push(m);
    if (match[1] === 'Tyre') e.tyre = m;
  });

  const out: BayWheel[] = [];
  const wbox = new THREE.Box3();
  const wc = new THREE.Vector3();
  const size = new THREE.Vector3();
  const invParent = new THREE.Matrix4();
  for (const { meshes, tyre } of groups.values()) {
    const anchor = tyre ?? meshes[0];
    const parent = anchor.parent;
    if (!parent) continue;
    wbox.setFromObject(anchor); // hub = the tyre's world-space centre
    wbox.getCenter(wc);
    wbox.getSize(size);
    const hub = wc.clone().applyMatrix4(invHolder);
    // The tyre is a torus about the axle (local X), so its diameter is the
    // larger of the two cross-axis spans — measured, never assumed.
    const holderScale = holder.getWorldScale(new THREE.Vector3()).x || 1;
    const radius = Math.max(size.y, size.z) / 2 / holderScale;
    parent.updateWorldMatrix(true, false);
    invParent.copy(parent.matrixWorld).invert();
    const pivot = new THREE.Group();
    pivot.position.copy(wc).applyMatrix4(invParent); // world hub → parent-local
    parent.add(pivot);
    for (const m of meshes) pivot.attach(m); // reparent, keeping world transform
    out.push({
      pivot,
      axis: axleWorld.clone().transformDirection(invParent).normalize(),
      hub,
      radius,
    });
  }
  return out;
}

/** Soft radial contact shadow so cars sit ON the photo floor (LobbyStage). */
function shadowTexture(): THREE.Texture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 6, 64, 64, 62);
  grad.addColorStop(0, 'rgba(0,0,0,0.62)');
  grad.addColorStop(0.7, 'rgba(0,0,0,0.30)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  return t;
}

/** Turn a loaded model into a locked-bay ghost: dark repaint + translucent. */
function ghostify(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      const std = m as THREE.MeshStandardMaterial;
      std.transparent = true;
      std.opacity = GHOST_OPACITY;
      if ('envMapIntensity' in std) std.envMapIntensity = 0.6;
      if (std.emissive) std.emissive.multiplyScalar(0.15);
    }
  });
}

export class GarageStage {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(36, 16 / 9, 0.1, 300);
  private env: EnvironmentBuilder | null = null;
  private shadowTex: THREE.Texture | null = null;
  private bays: Bay[] = [];
  /** M113 — multiplier on every drawn pedestal-ring opacity (1 = the classic
   *  full-strength rings). The redesigned garage backdrop PAINTS its own gold
   *  turntable ring, so GarageScreen dims the stage's rings way down and the
   *  shop softens them; callers that never touch this (OnboardingScreen) keep
   *  the exact pre-M113 look. Contact shadows are never scaled. */
  private ringScale = 1;
  private raf: number | null = null;
  private lastT = 0;
  private raycaster = new THREE.Raycaster();
  private readonly reducedMotion =
    typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  private readonly onCanvasClick = (e: PointerEvent): void => {
    const id = this.pick(e.clientX, e.clientY);
    if (id) this.onPick?.(id);
  };

  /** Screen callback: a car (locked or not) was tapped on the canvas. */
  onPick: ((vehicleId: string) => void) | null = null;

  constructor(private readonly host: HTMLElement) {}

  /**
   * Loading round: create the WebGL context + showroom IBL + warm the vehicle
   * model parse cache at IDLE (the title calls this a couple of seconds after
   * boot), in separate macrotasks, so the first GARAGE tap finds everything
   * ready instead of paying for a context, a PMREM bake and seven model
   * parses inside the tap. Safe to call any time; no-op once warm.
   */
  async prewarm(): Promise<void> {
    if (this.prewarmed) return;
    this.prewarmed = true;
    span('garage.prewarm.renderer', () => this.ensureRenderer());
    await yieldToMain();
    span('garage.prewarm.env', () => this.ensureEnv());
    await yieldToMain();
    for (const id of Object.keys(VEHICLES)) {
      const def = VEHICLES[id];
      if (!def?.url) continue;
      try {
        await modelLoader.load(def.url);
      } catch {
        /* a missing model falls back to the procedural body at show time */
      }
      await yieldToMain();
    }
  }
  private prewarmed = false;

  /** Showroom IBL so clearcoat paint + chrome reflect something (M35 lesson).
   *  Split out of ensureRenderer (loading round) so prewarm can bake it in
   *  its own task — a PMREM bake is the expensive half of stage creation. */
  private ensureEnv(): void {
    if (this.env || !this.renderer) return;
    this.env = new EnvironmentBuilder(this.renderer);
    this.scene.environment = span('garage.env', () =>
      this.env!.generate({
        skyTop: 0x232a38,
        skyHorizon: 0x4a5468,
        sun: 0xfff2dc,
        sunIntensity: 2.6,
        sunDir: new THREE.Vector3(0.2, 0.9, 0.35).normalize(),
        ground: 0x07080c,
      }),
    );
  }

  private ensureRenderer(): void {
    if (this.renderer) return;
    const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    r.setClearColor(0x000000, 0);
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.domElement.className = 'garage-canvas';
    this.host.appendChild(r.domElement);
    r.domElement.addEventListener('pointerdown', this.onCanvasClick);
    this.renderer = r;

    // Pit-garage grade: bright neutral overheads, warm key, red kick from the
    // left wall's LED strips + a cool white kick from the right (matches bg.webp).
    this.scene.add(new THREE.HemisphereLight(0xd8e2f2, 0x0d0f16, 0.85));
    const key = new THREE.DirectionalLight(0xfff4e0, 2.2);
    key.position.set(5, 12, 10);
    this.scene.add(key);
    const rim = new THREE.DirectionalLight(0xaebfe0, 1.0);
    rim.position.set(-6, 7, -11);
    this.scene.add(rim);
    const redKick = new THREE.DirectionalLight(0xff2a22, 0.6);
    redKick.position.set(-14, 3, 2);
    this.scene.add(redKick);
    const coolKick = new THREE.DirectionalLight(0x9fc2ff, 0.55);
    coolKick.position.set(14, 4, -2);
    this.scene.add(coolKick);

    // Fixed hero camera — MUST match the Blender GarageBG render camera so the
    // 3D floor and the photo floor agree (fov 36, y 4.1, look-at height 1.3).
    this.camera.position.set(0, 4.1, 20);
    this.camera.lookAt(0, this.lookY, 0);

    this.shadowTex = shadowTexture();
  }

  /** Build one bay per vehicle (models load async; safe to call per show). */
  setVehicles(defs: GarageBayDef[]): void {
    this.ensureRenderer();
    this.ensureEnv();
    for (const b of this.bays) this.scene.remove(b.group);
    this.bays = [];
    this.wheelFocus = null; // M115 — a rebuilt lineup starts un-focused
    this.wheelReq = null;
    this.soloId = null; // …and fully visible
    defs.forEach((def, i) => {
      const group = new THREE.Group();

      const shadowMat = new THREE.MeshBasicMaterial({
        map: this.shadowTex,
        transparent: true,
        depthWrite: false,
        opacity: def.unlocked ? 1 : 0.4,
      });
      const shadow = new THREE.Mesh(new THREE.PlaneGeometry(7.4, 7.4), shadowMat);
      shadow.rotation.x = -Math.PI / 2;
      shadow.position.y = 0.015;
      group.add(shadow);

      const ringMat = new THREE.MeshBasicMaterial({
        color: def.unlocked ? new THREE.Color(ACCENTS[def.id] ?? '#ff4438') : 0x2a3140,
        transparent: true,
        opacity: (def.unlocked ? 0.55 : 0.16) * this.ringScale,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const ring = new THREE.Mesh(new THREE.RingGeometry(2.6, 2.95, 56), ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.03;
      group.add(ring);

      const carHolder = new THREE.Group();
      carHolder.rotation.y = LINEUP_YAW;
      group.add(carHolder);
      this.scene.add(group);

      const bay: Bay = {
        id: def.id,
        unlocked: def.unlocked,
        look: def.look,
        rig: null,
        group,
        carHolder,
        modelRoot: null,
        paintColor: 0,
        ringMat,
        shadowMat,
        loadToken: 0,
        target: new THREE.Vector3(),
        yawTarget: LINEUP_YAW,
        spinPhase: 0.9 * i,
        isHero: false,
        placed: false,
        wheels: [],
        wheelPhase: 0,
      };
      this.bays.push(bay);
      this.loadCar(bay);
    });
  }

  /** Loading round: bays are dressed ONE PER MACROTASK (hero first) instead of
   *  all seven in one microtask flush — with the GLBs already in the parse
   *  cache (a race loaded them), the old path cloned, fitted and re-skinned
   *  the whole lineup inside the GARAGE tap's own task: a 700+ ms stall at
   *  phone speeds, i.e. "the garage click didn't register". */
  private loadQueue: Bay[] = [];
  private pumping = false;

  private loadCar(bay: Bay): void {
    bay.loadToken++;
    if (!this.loadQueue.includes(bay)) this.loadQueue.push(bay);
    void this.pumpLoads();
  }

  private async pumpLoads(): Promise<void> {
    if (this.pumping) return;
    this.pumping = true;
    try {
      while (this.loadQueue.length) {
        const heroIdx = this.loadQueue.findIndex((b) => b.isHero);
        const bay = this.loadQueue.splice(heroIdx >= 0 ? heroIdx : 0, 1)[0];
        await this.loadOne(bay);
        await yieldToMain();
      }
    } finally {
      this.pumping = false;
    }
  }

  /** Loading round: warm the model parse cache + compile this bay's shaders
   *  off the first painted frame where possible (compileAsync is fire-and-
   *  forget — the rAF loop's next draw finds the programs linked or linking). */
  private async loadOne(bay: Bay): Promise<void> {
    const def = VEHICLES[bay.id] ?? VEHICLES.sports;
    const token = bay.loadToken;
    // M151: the car wears its real STOCK livery (vehicles.ts stockColor) —
    // the catalog ACCENT is a menu colour and may differ from the paint (the
    // black muscle car's accent is steel so its chips stay readable; painting
    // the car with it turned the car silver, measured in the lineup).
    const paint = bay.unlocked ? stockColorOf(bay.id) : GHOST_PAINT;
    bay.paintColor = paint;
    await modelLoader
      .load(def.url)
      .then((g) => {
        if (token !== bay.loadToken) return;
        const t0 = performance.now();
        const fitted = ModelLoader.fit(g, def.length, def.forwardIsZ);
        // M114b: the vehicle id (and so its stock paint treatment) only rides
        // along for an UNLOCKED bay — a locked ride must stay the flat dark
        // GHOST_PAINT, and a gradient map would discard that colour entirely.
        applyCarMaterials(
          fitted,
          paint,
          bay.unlocked ? bay.look : undefined,
          bay.unlocked ? bay.id : undefined,
        );
        if (!bay.unlocked) ghostify(fitted);
        bay.rig?.dispose();
        bay.carHolder.clear();
        bay.carHolder.add(fitted);
        bay.modelRoot = fitted;
        bay.surface = surfaceFromObject(fitted, bay.carHolder); // M128c
        // M128: the mods the bay's look carries (never on a locked ghost).
        bay.rig = bay.unlocked ? attachModRig(bay.carHolder, bay.look, bay.id, paint, bay.surface) : null;
        // M115: wrap the model wheels so the close-up can frame + roll one.
        bay.wheels = collectWheels(fitted, bay.carHolder);
        // A close-up asked for before the model landed applies now.
        if (this.wheelReq && this.wheelReq.id === bay.id) {
          this.setWheelCloseUp(bay.id, this.wheelReq.t);
        }
        recordSpan('garage.dressCar', performance.now() - t0);
        if (this.renderer) void this.renderer.compileAsync(fitted, this.camera, this.scene);
      })
      .catch(() => {
        if (token !== bay.loadToken) return;
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(1.9, 0.9, 4),
          new THREE.MeshStandardMaterial({ color: paint, roughness: 0.35, metalness: 0.5 }),
        );
        body.position.y = 0.75;
        if (!bay.unlocked) ghostify(body);
        bay.carHolder.clear();
        bay.carHolder.add(body);
        bay.wheels = []; // no named wheels on the fallback body — no close-up
      });
  }

  /** M79 shop preview: re-skin a loaded bay's car with a new look, in place
   *  (no model reload — a cheap material re-apply on the kept GLB root). */
  setLook(id: string, look: CarLook | undefined): void {
    const bay = this.bays.find((b) => b.id === id);
    if (!bay) return;
    bay.look = look;
    if (bay.modelRoot && bay.unlocked) {
      applyCarMaterials(bay.modelRoot, bay.paintColor, look, bay.id);
      bay.rig?.dispose();
      bay.rig = attachModRig(bay.carHolder, look, bay.id, bay.paintColor, bay.surface ?? null);
    }
  }

  /** Mark the active ride — it spins on the pedestal; everyone else parks. */
  setHero(id: string): void {
    for (const bay of this.bays) {
      const hero = bay.id === id;
      if (hero && !bay.isHero) bay.spinPhase = LINEUP_YAW; // spin on from the parked angle
      bay.isHero = hero;
      if (!hero) bay.yawTarget = LINEUP_YAW;
      // M102: locked rides can take the pedestal now (browse-only) — the
      // ghost stays dark, but its ring/shadow lift a touch so it reads focused.
      bay.ringMat.opacity = this.ringOpacityFor(bay);
      bay.shadowMat.opacity = bay.unlocked ? (hero ? 1 : 0.85) : hero ? 0.62 : 0.4;
    }
  }

  /** The M102 ring ladder × the M113 scale (soft contact shadow untouched). */
  private ringOpacityFor(bay: Bay): number {
    const base = bay.unlocked ? (bay.isHero ? 1.0 : 0.45) : bay.isHero ? 0.34 : 0.16;
    return base * this.ringScale;
  }

  /** M113 (additive, optional): dim/restore every drawn pedestal ring. Safe to
   *  call any time — before OR after setVehicles/setHero. Clamped 0..1. */
  setRingScale(scale: number): void {
    this.ringScale = Math.min(1, Math.max(0, scale));
    for (const bay of this.bays) bay.ringMat.opacity = this.ringOpacityFor(bay);
  }

  /** M115 (additive, optional): show ONLY this bay and hide the rest of the
   *  lineup — the garage's CUSTOMIZE overlay, which frames its car with a real
   *  fov zoom now (the shop's composition). A narrow fov crowds every
   *  ground-anchored bay toward the view axis, so the parked rides would pile
   *  up behind the hero instead of sitting small and far away as they did at
   *  fov zoom 1. Pass null to bring the lineup back. */
  setSolo(id: string | null): void {
    if (this.soloId === id) return;
    this.soloId = id;
    for (const bay of this.bays) bay.group.visible = !id || bay.id === id;
  }

  private soloId: string | null = null;

  /** M113b (additive, optional): dolly-free zoom — narrows the fov so the same
   *  anchored floor spot renders larger (the portrait shop's hero; since M114
   *  also the garage hero and the shop's wheel close-up). Anchors stay exact
   *  because applyTargets unprojects through the live camera — call this
   *  BEFORE the layout pass that should see it. Clamped 1..14: everything that
   *  still uses the GROUND anchor stays at/below ~3 (past that the ground
   *  plane fills so little of a very narrow frame that high anchors get hard
   *  to land), while the M115 WHEEL CLOSE-UP runs way up the range because it
   *  places the car from a measured hub instead of the ground unproject. */
  setFovZoom(zoom: number): void {
    const z = Math.min(14, Math.max(1, zoom));
    if (z === this.fovZoom) return;
    this.fovZoom = z;
    this.camera.fov = 36 / z; // 36 = the ctor's base fov (matched to the bg art)
    this.camera.updateProjectionMatrix();
  }

  private fovZoom = 1;

  /** M114 (additive, optional): a hero-only presentation LIFT — the active
   *  ride renders scale× while parked rides stay 1, so the garage hero can
   *  fill the stage (owner: "fills most of the distance between the arrows")
   *  without fov-inflating the whole lineup. Cars never share a physical
   *  space here, so relative scale is a free showroom trick. Glides with the
   *  same damp as position; callers that never touch this keep 1.
   *  LENGTH-NORMALIZED (measured): a flat 1.55 was perfect on the 3.8u coupe
   *  but made the 4.3u monster truck swallow the whole stage — the effective
   *  scale is capped so scale×length never exceeds HERO_SPAN (the coupe's
   *  tuned on-screen span), so every hero lands at a similar visual size. */
  setHeroScale(scale: number): void {
    this.heroScale = Math.min(1.8, Math.max(1, scale));
  }

  private heroScale = 1;

  private heroScaleFor(bay: Bay): number {
    const len = VEHICLES[bay.id]?.length ?? 4;
    return Math.min(this.heroScale, Math.max(1, HERO_SPAN / len));
  }

  /** M114 (additive, optional): re-aim the fixed camera's look-at HEIGHT.
   *  Lowering it pitches the view down toward the wheels, so a high fov zoom
   *  frames TIRES & RIMS instead of the greenhouse (the shop's true wheel
   *  close-up — owner note). Anchors stay screen-exact (applyTargets
   *  unprojects through the live camera). Default 1.3 = the Blender GarageBG
   *  match — callers that never touch this keep the classic framing.
   *  M115 widened the floor to 0.05 so the WHEEL CLOSE-UP can aim at a real
   *  measured hub height (a monster-truck hub is high, a formula hub is low). */
  setLookY(y: number): void {
    const v = Math.min(2, Math.max(0.05, y));
    if (v === this.lookY) return;
    this.lookY = v;
    if (this.renderer) this.camera.lookAt(0, this.lookY, 0);
  }

  private lookY = 1.3;

  /** M115 — the live WHEEL CLOSE-UP (bay id) + the last request, so a close-up
   *  asked for while the GLB was still downloading applies when it lands. */
  private wheelFocus: string | null = null;
  private wheelReq: { id: string; t: WheelCloseUp } | null = null;

  /**
   * M115 (owner: "an ultra close up on one of the car's tire and rim… without
   * the car rotating") — frame ONE wheel of `id`.
   *
   * Pass `null` to leave the mode (the hero resumes its pedestal spin and the
   * caller goes back to setLookY/setFovZoom/applyTargets as usual).
   *
   * Returns FALSE when the bay has no named wheels — the procedural fallback
   * body, or any future model that misses the M79c naming contract — so the
   * caller can keep the normal whole-car framing instead of showing nothing.
   *
   * How it frames: the camera stays exactly where the garage backdrop expects
   * it (moving it would break the painted-floor match), and we instead (a) aim
   * its look-at at the measured hub height, (b) pick the fov that makes the
   * measured tyre diameter fill `fill` of the viewport height, and (c) SLIDE
   * THE CAR so that hub lands on the requested viewport fraction — solved by
   * unprojecting the wanted screen point at the hub's own camera depth, so the
   * landing is exact for every vehicle regardless of wheelbase or wheel size
   * (the M114 "a flat number was perfect on the coupe and wrong on the monster
   * truck" lesson, solved by measuring instead of guessing).
   */
  setWheelCloseUp(id: string, t: WheelCloseUp | null): boolean {
    const bay = this.bays.find((b) => b.id === id);
    if (!bay) return false;
    if (!t) {
      this.wheelReq = null;
      if (this.wheelFocus === id) this.wheelFocus = null;
      return true;
    }
    this.wheelReq = { id, t };
    if (!this.renderer || bay.wheels.length === 0) return false;

    // Pick the wheel that ends up FRONT and NEAREST the camera once parked
    // side-on: local +X faces the camera at WHEEL_YAW, local +Z is the nose.
    const wheel = bay.wheels.reduce((best, w) =>
      w.hub.x + w.hub.z * 0.5 > best.hub.x + best.hub.z * 0.5 ? w : best,
    );
    const scale = bay.isHero ? this.heroScaleFor(bay) : 1;
    // The hub's offset from the bay's ground origin once parked side-on (the
    // bay group itself never rotates, so this is a plain world offset).
    const off = wheel.hub
      .clone()
      .applyMatrix4(new THREE.Matrix4().makeRotationY(WHEEL_YAW))
      .multiplyScalar(scale);
    const hubY = Math.max(0.05, off.y);
    const diameter = wheel.radius * 2 * scale;

    this.wheelFocus = id;
    this.setLookY(hubY);
    // Two passes: the first sizes the lens from the on-axis distance, the
    // second re-sizes from where the wheel actually ended up (an off-centre
    // anchor sits slightly further away, which would read slightly small).
    const ndc = new THREE.Vector3();
    const want = new THREE.Vector3();
    const axis = new THREE.Vector3(0, hubY, 0);
    for (let pass = 0; pass < 2; pass++) {
      const dist =
        pass === 0
          ? this.camera.position.distanceTo(axis)
          : this.camera.position.distanceTo(want);
      const need = (2 * Math.atan(diameter / 2 / dist) * 180) / Math.PI / Math.max(0.05, t.fill);
      this.setFovZoom(36 / need);
      this.camera.updateMatrixWorld();
      ndc.set(0, hubY, 0).project(this.camera);
      want.set(t.xFrac * 2 - 1, -(t.yFrac * 2 - 1), ndc.z).unproject(this.camera);
    }
    bay.target.copy(want).sub(off);
    bay.group.position.copy(bay.target); // a lens change, not a drive — snap
    bay.placed = true;
    bay.carHolder.rotation.y = WHEEL_YAW;
    bay.spinPhase = WHEEL_YAW; // so leaving the mode resumes from here
    return true;
  }

  /** Screen-driven placement (M64): unproject each viewport-fraction target
   *  onto the ground plane. Bays glide to new spots in the tick. */
  applyTargets(targets: Record<string, BayTarget>): void {
    if (!this.renderer) return;
    this.camera.updateMatrixWorld();
    for (const bay of this.bays) {
      const t = targets[bay.id];
      if (!t) continue;
      if (this.wheelFocus === bay.id) continue; // M115 close-up owns this bay
      if (this.soloId && bay.id !== this.soloId) continue; // M115 — hidden
      const ndc = new THREE.Vector3(t.xFrac * 2 - 1, -(t.yFrac * 2 - 1), 0.5).unproject(
        this.camera,
      );
      const dir = ndc.sub(this.camera.position).normalize();
      if (dir.y >= -0.02) continue; // above the horizon — can't land on the floor
      const k = -this.camera.position.y / dir.y;
      const p = this.camera.position.clone().addScaledVector(dir, k);
      bay.target.set(p.x, 0, p.z);
      if (!bay.placed || this.reducedMotion) {
        bay.group.position.copy(bay.target); // first placement snaps
        bay.placed = true;
      }
    }
  }

  /** Raycast a canvas click to the vehicle it landed on (or null). */
  pick(clientX: number, clientY: number): string | null {
    if (!this.renderer) return null;
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -(((clientY - rect.top) / rect.height) * 2 - 1),
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    for (const hit of this.raycaster.intersectObjects(
      // M115: a solo'd lineup must not be pickable through the hidden cars
      // (three's Raycaster stopped honouring `visible` back in r125).
      this.bays.filter((b) => b.group.visible).map((b) => b.carHolder),
      true,
    )) {
      let o: THREE.Object3D | null = hit.object;
      while (o) {
        const bay = this.bays.find((b) => b.carHolder === o);
        if (bay) return bay.id;
        o = o.parent;
      }
    }
    return null;
  }

  show(): void {
    this.ensureRenderer();
    this.ensureEnv();
    if (this.renderer) this.renderer.domElement.style.display = '';
    this.resize();
    if (this.raf === null) {
      this.lastT = performance.now();
      const tick = (): void => {
        this.raf = requestAnimationFrame(tick);
        const now = performance.now();
        const dt = Math.min(0.1, (now - this.lastT) / 1000);
        this.lastT = now;
        const ease = this.reducedMotion ? 1 : 1 - Math.exp(-GLIDE * dt);
        for (const b of this.bays) {
          // M128: mods idle on the pedestal (fans cheer, flags hang, lamps on).
          b.rig?.update(0, 0, 0, false, false, dt);
          b.group.position.lerp(b.target, ease);
          // M114 hero lift — glide the whole bay group's scale (ring + shadow
          // ride along, so the pedestal furniture keeps hugging the car).
          const sTarget = b.isHero ? this.heroScaleFor(b) : 1;
          const s = b.group.scale.x + (sTarget - b.group.scale.x) * ease;
          b.group.scale.setScalar(s);
          if (this.wheelFocus === b.id) {
            // M115 close-up: the CAR is still (a spin swings the wheel out of
            // frame) and only the wheel turns, on the spot.
            b.carHolder.rotation.y = WHEEL_YAW;
            if (!this.reducedMotion) b.wheelPhase += dt * WHEEL_SPIN;
            for (const w of b.wheels) w.pivot.quaternion.setFromAxisAngle(w.axis, b.wheelPhase);
          } else if (b.isHero) {
            if (!this.reducedMotion) b.spinPhase += dt * ROT_SPEED;
            b.carHolder.rotation.y = b.spinPhase;
          } else {
            // Park back at the lineup angle via the shortest way round.
            let cur = b.carHolder.rotation.y;
            const twoPi = Math.PI * 2;
            cur -= twoPi * Math.round((cur - b.yawTarget) / twoPi);
            b.carHolder.rotation.y = cur + (b.yawTarget - cur) * ease;
            b.spinPhase = b.carHolder.rotation.y;
          }
        }
        this.renderer?.render(this.scene, this.camera);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  hide(): void {
    if (this.raf !== null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    if (this.renderer) this.renderer.domElement.style.display = 'none';
  }

  resize(): void {
    if (!this.renderer) return;
    const w = Math.max(1, this.host.clientWidth);
    const h = Math.max(1, this.host.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  dispose(): void {
    for (const b of this.bays) b.rig?.dispose();
    this.hide();
    this.renderer?.domElement.removeEventListener('pointerdown', this.onCanvasClick);
    this.env?.dispose();
    this.shadowTex?.dispose();
    this.scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) for (const m of mat) m.dispose();
      else if (mat) mat.dispose();
    });
    this.renderer?.dispose();
    this.renderer?.domElement.remove();
    this.renderer = null;
  }
}
